import { useMemo } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { ProfileFilter } from '../components/ProfileFilter';
import { RankedList } from '../components/RankedList';
import { exportCardCsv } from '../utils/csvExport';
import { computeRealCommunicationTotals } from '../utils/realCommunicationAnalytics';

export function CommunicationModule() {
  const { filteredProfiles, profilesLoading } = useDashboard();

  const analytics = useMemo(
    () => computeRealCommunicationTotals(filteredProfiles),
    [filteredProfiles]
  );

  const categoriesExplorationPct =
    analytics.totalCategoriesAvailable > 0
      ? Math.round((analytics.categoriesUsed / analytics.totalCategoriesAvailable) * 100)
      : 0;
  const subcategoriesExplorationPct =
    analytics.totalSubcategoriesAvailable > 0
      ? Math.round((analytics.subcategoriesUsed / analytics.totalSubcategoriesAvailable) * 100)
      : 0;

  return (
    <div>
      <ProfileFilter />

      {!profilesLoading && filteredProfiles.length === 0 && (
        <p className="text-sm text-muted-foreground mb-6">
          Nenhum perfil encontrado para esse filtro no Firestore.
        </p>
      )}

      <div className="mb-6 pb-4 border-b border-border flex flex-wrap gap-8">
        <div>
          <p className="text-xs text-muted-foreground">Categorias disponíveis</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.totalCategoriesAvailable}
          </p>
          <p className="text-[10px] text-muted-foreground">10 app + {analytics.totalCategoriesAvailable - 10} criadas</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Subcategorias disponíveis</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.totalSubcategoriesAvailable}
          </p>
          <p className="text-[10px] text-muted-foreground">86 app + {analytics.totalSubcategoriesAvailable - 86} criadas</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Categorias exploradas</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.categoriesUsed} / {analytics.totalCategoriesAvailable}
            <span className="text-sm font-normal text-muted-foreground ml-1">
              ({categoriesExplorationPct}%)
            </span>
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Subcategorias exploradas</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.subcategoriesUsed} / {analytics.totalSubcategoriesAvailable}
            <span className="text-sm font-normal text-muted-foreground ml-1">
              ({subcategoriesExplorationPct}%)
            </span>
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Total de cliques (acumulado)</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.totalClicks.toLocaleString('pt-BR')}
          </p>
        </div>
      </div>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Ranking de interesses — totais acumulados
        </h3>
        {/* Categorias ocupa toda a largura; subcategorias fica ao lado do gráfico de grupos */}
        <RankedList
          title="Categorias clicadas"
          rows={analytics.topCategories.map((r) => ({
            label: r.name,
            value: r.value.toLocaleString('pt-BR'),
            pct: analytics.totalClicks > 0 ? (r.value / analytics.totalClicks) * 100 : 0,
          }))}
          onExport={() =>
            exportCardCsv(
              'comunicacao-top-categorias',
              ['Categoria', 'Cliques'],
              analytics.topCategories.map((r) => [r.name, r.value])
            )
          }
        />
        <div className="mt-4">
          <RankedList
            title="Subcategorias clicadas"
            rows={analytics.topSubcategories.map((r) => ({
              label: r.name,
              value: r.value.toLocaleString('pt-BR'),
              pct: analytics.totalClicks > 0 ? (r.value / analytics.totalClicks) * 100 : 0,
            }))}
            onExport={() =>
              exportCardCsv(
                'comunicacao-top-subcategorias',
                ['Subcategoria', 'Cliques'],
                analytics.topSubcategories.map((r) => [r.name, r.value])
              )
            }
          />
        </div>
      </section>

      {analytics.userCreatedCategories.length > 0 && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            Categorias criadas pelos participantes ({analytics.userCreatedCategories.length})
          </h3>
          <RankedList
            title="Categorias personalizadas"
            rows={analytics.userCreatedCategories.map((name) => ({ label: name, value: '—' }))}
          />
        </section>
      )}

      {analytics.userCreatedSubcategories.length > 0 && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            Subcategorias criadas pelos participantes ({analytics.userCreatedSubcategories.length})
          </h3>
          <RankedList
            title="Subcategorias personalizadas"
            rows={analytics.userCreatedSubcategories.map((s) => ({
              label: s.name,
              value: s.categoryName,
            }))}
            onExport={() =>
              exportCardCsv(
                'comunicacao-subcategorias-criadas',
                ['Subcategoria', 'Categoria'],
                analytics.userCreatedSubcategories.map((s) => [s.name, s.categoryName])
              )
            }
          />
        </section>
      )}
    </div>
  );
}
